import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * Last-resort boundary so a render-time throw shows something actionable
 * instead of a white screen.
 *
 * Deliberately NOT a retry button: whatever threw during render will usually
 * throw again on the same state, and a button that appears to fix nothing is
 * worse than one that is not there. Reload is honest — it drops the state that
 * produced the throw.
 */
export class ConsoleErrorBoundary extends Component<
  { children: ReactNode },
  { error: Error | null }
> {
  override state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    // Console is the only sink here — there is no client error reporter wired
    // up, and inventing one is out of scope for this task.
    console.error('[console] render error', error, info.componentStack);
  }

  override render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center gap-3 p-21">
        <h1 className="text-2xl font-semibold">Something broke on this page</h1>
        <p className="text-sm text-text-secondary">
          The console hit an error it could not render around. Reloading usually clears it. If it
          keeps happening on the same page, that is a bug worth reporting with what you were doing.
        </p>
        <pre className="overflow-x-auto rounded border border-border bg-surface-sunken p-13 font-mono text-[12px] text-text-secondary">
          {this.state.error.message}
        </pre>
        <div>
          <button
            type="button"
            className="btn"
            onClick={() => globalThis.location.reload()}
          >
            Reload the console
          </button>
        </div>
      </div>
    );
  }
}
